import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { AxiosResponse } from "axios";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import { Utility } from "../../core/Utility";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { DateInput } from "../../components/DateInput/DateInput";
import { PdfPreview } from "../../components/PdfPreview/PDFPreview";
import { getAnnouncedVehiclesReportTemplate } from "../../document-templates/AnnouncedVehicleReportTemplate";
import { ReportAnnouncedVehicles } from "../../models/Report";
import { ReceiveStatus } from "../../models/DocumentOfReceive";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { useApplicationContext } from "../../application/ApplicationContext";
import { getAllDocuments } from "../../services/NewVersionService";
import "./AnnouncedVehiclesReportPage.scss";

export const AnnouncedVehiclesReportPage = () => {
  const { t } = useTranslation();

  const { state } = useApplicationContext();
  const businessYear = state.businessYear ?? new Date().getFullYear();
  const yearStart = new Date(businessYear, 0, 1);
  const yearEnd = new Date(businessYear, 11, 31);

  const getDefaultDateRangeForBusinessYear = () => {
    const today = new Date();
    if (businessYear === today.getFullYear()) {
      // poslednjih mesec dana, ali ne pre 1.1. poslovne godine
      const oneMonthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
      const from = oneMonthAgo < yearStart ? yearStart : oneMonthAgo;

      return {
        from,
        to: today,
      };
    }

    // za “stare” poslovne godine: 1.1 – 31.12
    return {
      from: yearStart,
      to: yearEnd,
    };
  };
  const defaultRange = getDefaultDateRangeForBusinessYear();
  const [dateFrom, setDateFrom] = useState<Date>(defaultRange.from);
  const [dateTo, setDateTo] = useState<Date>(defaultRange.to);
  const [reportData, setReportData] = useState<ReportAnnouncedVehicles[]>([]);
  const [processing, setProcessing] = useState(false);
  const [changeState, setChangeState] = useState(false);

  const [pdfData, setPdfData] = useState<any>(
    getAnnouncedVehiclesReportTemplate(reportData)
  );

  const downloadPdfDocument = useRef(null);
  const printPdfDocument = useRef(null);

  useEffect(() => {
    const run = async () => {
      setProcessing(true);
      await formatReportData();
      setProcessing(false);
    };
    run();
  }, []);

  useEffect(() => {
    const range = getDefaultDateRangeForBusinessYear();
    setDateFrom(range.from);
    setDateTo(range.to);
    setChangeState((prev) => !prev);
  }, [businessYear]);

  useEffect(() => {
    setReportData([]);
    setChangeState((prev) => !prev);
  }, [dateFrom, dateTo]);

  useEffect(() => {
    const run = async () => {
      setProcessing(true);
      await formatReportData();
      setProcessing(false);
    };
    run();
  }, [changeState]);

  // populate pdf when data changes
  useEffect(() => {
    const newPdf = getAnnouncedVehiclesReportTemplate(reportData);
    setPdfData(newPdf);
  }, [reportData]);

  const startOfDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate());

  const startOfNextDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);

  // uklanjanje vremena, za poređenje po danu
  const stripTimeMs = (d: Date | string) => {
    const dt = new Date(d);
    return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()).getTime();
  };

  const formatReportData = async () => {
    const apiFrom = startOfDay(dateFrom);
    const apiToExclusive = startOfNextDay(dateTo);

    const fromMs = stripTimeMs(dateFrom);
    const toMs = stripTimeMs(dateTo);

    const [inputsResponse, outputsResponse]: [AxiosResponse, AxiosResponse] =
      await Promise.all([
        getAllDocuments(true, true, apiFrom, apiToExclusive), // prijemnice
        getAllDocuments(true, false, apiFrom, apiToExclusive), // otpremnice
      ]);

    const inputDocs = inputsResponse.data?.result ?? [];
    const outputDocs = outputsResponse.data?.result ?? [];

    // mapiranje dokumenata u redove tabele (bez spajanja)
    const rows: ReportAnnouncedVehicles[] = [];

    const shouldIncludeDoc = (doc: any) => {
      const day = stripTimeMs(doc.date);
      if (day < fromMs || day > toMs) return false; // sigurnosni filter po datumu
      if (
        doc?.status === ReceiveStatus.Finished ||
        doc?.status === ReceiveStatus.Arrived
      ) {
        return false; // već završeni / stigli nas ne zanimaju
      }
      return true;
    };

    for (const doc of inputDocs) {
      if (!shouldIncludeDoc(doc)) continue;

      rows.push({
        id: doc.id,
        date: new Date(doc.date),
        type: t("ISTOVAR"),
        shipperName: doc.shipperName ?? "",
        vehicleNumber: (doc.vehicleNumber ?? "").toUpperCase(),
        productName: doc.productName ?? "",
      });
    }

    for (const doc of outputDocs) {
      if (!shouldIncludeDoc(doc)) continue;

      rows.push({
        id: doc.id,
        date: new Date(doc.date),
        type: t("UTOVAR"),
        shipperName: doc.shipperName ?? "",
        vehicleNumber: (doc.vehicleNumber ?? "").toUpperCase(),
        productName: doc.productName ?? "",
      });
    }

    // spajanje UTOVAR/ISTOVAR po danu + vozilu

    const merged = new Map<string, ReportAnnouncedVehicles>();

    for (const row of rows) {
      const dayKey = stripTimeMs(row.date); // isti dan
      const key = `${dayKey}|${row.vehicleNumber}`;

      const existing = merged.get(key);

      if (!existing) {
        merged.set(key, row);
        continue;
      }

      // već postoji drugi “par” za isti dan i isto vozilo → UTOVAR ISTOVAR
      merged.set(key, {
        ...existing,
        type: t("UTOVAR ISTOVAR"),
      });
    }

    const uniqueDailyData = Array.from(merged.values()).sort((a, b) =>
      a.date < b.date ? 1 : -1
    );

    setReportData(uniqueDailyData);
  };

  const formatDate = (rowData: any) => {
    return Utility.formatDate(new Date(rowData.date));
  };

  const formatType = (rowData: any) => {
    switch (rowData.type) {
      case t("UTOVAR"):
        return <span className="input-vehicles-type">{t("UTOVAR")}</span>;
      case t("ISTOVAR"):
        return <span className="output-vehicles-type">{t("ISTOVAR")}</span>;
      case t("UTOVAR ISTOVAR"):
        return (
          <div>
            <span className="input-vehicles-type">{t("UTOVAR")}</span>
            <span className="output-vehicles-type">{t("ISTOVAR")}</span>
          </div>
        );
    }
  };

  const {
    control,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    mode: "all",
  });

  return (
    <PageLayout>
      <div className="table-wrapper">
        <div className="table-header">
          <div className="header-left">
            <i className="bi bi-truck announced-title-icon"></i>
            <span className="header-title">
              {t("Najavljena vozila za prevoz merkantilne robe")}
            </span>
            <span className="header-elements">{t("od")}</span>
            <DateInput
              id="dateFrom"
              value={dateFrom}
              onValueChange={(_, value) => setDateFrom(value as Date)}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
            <span className="header-elements">{t("do")}</span>
            <DateInput
              id="dateTo"
              value={dateTo}
              onValueChange={(_, value) => setDateTo(value as Date)}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
          </div>
          <div className="header-right">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={""}
              icon="bi bi-file-earmark-pdf"
              onButtonClick={() => {
                if (downloadPdfDocument.current) {
                  // @ts-ignore
                  downloadPdfDocument.current();
                }
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
              }}
              labelText={""}
              icon="bi bi-printer"
              onButtonClick={() => {
                if (printPdfDocument.current) {
                  // @ts-ignore
                  printPdfDocument.current();
                }
              }}
            />
          </div>
        </div>
        <div className="wrapper-data-table">
          {processing ? (
            <ProgressSpinner
              style={{ width: "100px", height: "100px", color: "green" }}
              strokeWidth="4"
              animationDuration=".8s"
            />
          ) : (
            <DataTable value={reportData} showGridlines stripedRows>
              <Column body={formatDate} header={t("Datum")} />
              <Column field="shipperName" header={t("Prevoznik")} />
              <Column field="vehicleNumber" header={t("Reg.broj vozila")} />
              <Column field="productName" header={t("Roba")} />
              <Column body={formatType} header={t("Tip dovoza")} />
            </DataTable>
          )}
        </div>
        <div className="document-preview">
          <PdfPreview
            pdfDefinition={pdfData}
            downloadDocument={downloadPdfDocument}
            printDocument={printPdfDocument}
            documentName={`${t("Najavljena vozila")} ${Utility.formatDate(
              new Date()
            )}.pdf`}
          />
        </div>
      </div>
    </PageLayout>
  );
};
